import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { applyQueryLanguage, applyPathLanguage } from '../i18n';
import { auth } from '../firebase';
import { ROUTES } from '../constants/Routes';
import { exchangeAuthCode, setLanguage as apiSetLanguage } from '../utils/api';
import AuthLayout from '../components/auth/AuthLayout';
import { AUTH_LINK_CLASS } from '../components/auth/authLinkStyles';

/**
 * Auth callback page for public BiQ signup flow.
 *
 * After a user signs up on bloomiq.io (marketing site), they get redirected to:
 *   app.bloomiq.io/auth/callback#code=<auth_code>
 *
 * This page exchanges the one-time auth code for a Firebase custom token,
 * signs the user into bloom-dev, and redirects to BiQ.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // The marketing site now redirects to /:lang/auth/callback#code=XYZ
        // (path-based locale). Prefer that; fall back to the legacy
        // ?lang= query param for in-flight inbound links from old bloom-
        // website builds / cached pages. Either way we flip i18n immediately
        // and forward the value to Firestore via setLanguage once signed in.
        let lang = applyPathLanguage(window.location.pathname);
        if (!lang) {
          const queryParams = new URLSearchParams(window.location.search);
          lang = applyQueryLanguage(queryParams.get('lang'));
        }

        // If already authenticated, skip exchange and go to dashboard
        if (auth.currentUser) {
          navigate(ROUTES.BLOOMTERMINAL, { replace: true });
          return;
        }

        // Extract auth code from URL fragment
        const hash = window.location.hash;
        if (!hash) {
          setError('No auth code provided');
          return;
        }

        const params = new URLSearchParams(hash.slice(1));
        const code = params.get('code');
        // `!code` catches missing/empty. The literal string `"undefined"` slips
        // past truthy checks and used to reach the server, which would reject
        // it with "Invalid code format" and surface as a generic auth error.
        // The server expects a 32-char lowercase hex token (`secrets.token_hex(16)`).
        if (!code || code === 'undefined' || !/^[0-9a-f]{32}$/.test(code)) {
          setError('Authentication link is invalid or has expired — please log in manually');
          return;
        }

        // Exchange code for custom token (no Firebase auth needed for this call)
        const result = await exchangeAuthCode(code);
        if (!result?.custom_token) {
          setError('Failed to authenticate — please try logging in manually');
          return;
        }

        // Sign into bloom-dev with the custom token
        await signInWithCustomToken(auth, result.custom_token);

        // Persist the visitor's marketing-site language to Firestore.
        // The UI was already flipped by applyQueryLanguage above, and
        // setLanguage is an idempotent merge-write so this is safe.
        if (lang) {
          try {
            await apiSetLanguage(lang);
          } catch (setLangErr) {
            // Non-blocking: log and continue to the dashboard. The reconcile
            // effect in UserContext will retry on next load.
            console.warn('AuthCallback: setLanguage failed', setLangErr);
          }
        }

        // Redirect to BiQ
        navigate(ROUTES.BLOOMTERMINAL, { replace: true });
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('Authentication failed — please try logging in manually');
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <AuthLayout variant="light" align="center">
      <div className="flex flex-col items-center justify-center min-h-[300px]">
        {error ? (
          <div className="text-center">
            <p className="text-red-400 mb-4">{error}</p>
            <a
              href={ROUTES.LOGIN}
              className={AUTH_LINK_CLASS}
            >
              Go to Login
            </a>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <div className="flex gap-1.5">
              <span className="w-3 h-3 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-3 h-3 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-3 h-3 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <p className="text-[#282A29] text-lg">Signing you in...</p>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};

export default AuthCallback;
